import { cents, from } from '@nuts/auto-delivery-sdk/dist/utils/money';

import { getConfigEntry } from '@/api/config';
import { BaseCustomizer, SkuSelection } from '@/lib/customizers/common';
import { SkuIngredient } from '@/lib/customizers/ingredients';
import {
  DEFAULT_CHANNEL_KEY,
  DEFAULT_SUBSCRIPTION_CHANNEL_KEY,
  getListPrice,
  getMoneyValue,
  getPriceByChannelKey,
  PriceLike,
} from '@/lib/product/prices';
import { isDefined } from '@/utils/isDefined';
import { Money } from '@/utils/money';

export const ENCODING_DELIMITER = '~';

export const INGREDIENT_LIMITS = { min: 6, max: Infinity };

export class BundleCustomizer extends BaseCustomizer<SkuSelection> {
  prices: PriceLike[] = [
    { value: from(0) },
    { value: from(0), channel: { obj: { key: DEFAULT_SUBSCRIPTION_CHANNEL_KEY } } },
  ];

  priceChannelKey?: string = DEFAULT_CHANNEL_KEY;

  readonly type = 'Bundle';

  constructor(selections?: SkuSelection[], quantity?: number) {
    super(INGREDIENT_LIMITS, selections, quantity);
  }

  get basePrice() {
    return getPriceByChannelKey(this.prices, this.priceChannelKey);
  }

  /**
   * Derive the price from the {@link selections}.
   *
   * @returns a {@link Money} object representing the price of a single quantity of this bundle customizer
   */
  calculatePrice(applyGreatestHitsDiscount: boolean = false) {
    const selections = this.selections.filter(isDefined);

    let basePriceByPortion = this.currentPriceValue;
    if (cents(basePriceByPortion) && selections.length < INGREDIENT_LIMITS.min) {
      basePriceByPortion = Money.multiply(
        Money.divide(this.currentPriceValue, INGREDIENT_LIMITS.min),
        selections.length,
      );
    }

    const selectionsPrice = Money.sumBy(selections, (s) => {
      const price = getPriceByChannelKey(s.prices, this.priceChannelKey);
      return getMoneyValue(price, this.quantity);
    });

    const price = Money.add(basePriceByPortion, selectionsPrice);

    if (applyGreatestHitsDiscount) {
      const discountAmount = getConfigEntry('csb').discount.amount ?? 0;
      return Money.multiply(price, (100 - discountAmount) / 100);
    }

    return price;
  }

  /**
   * Derive the price before discount from the {@link selections}.
   *
   * @returns a {@link Money} object representing the price before discount of a single quantity of this bundle customizer
   */
  calculatePriceBeforeDiscount() {
    const selections = this.selections.filter(isDefined);

    let basePriceByPortion = this.currentPriceValue;
    if (cents(basePriceByPortion) && selections.length < INGREDIENT_LIMITS.min) {
      basePriceByPortion = Money.multiply(
        Money.divide(this.currentPriceValue, INGREDIENT_LIMITS.min),
        selections.length,
      );
    }

    const selectionsPrice = Money.sumBy(
      selections,
      (s) => getPriceByChannelKey(s.prices, this.priceChannelKey).value,
    );
    return Money.add(basePriceByPortion, selectionsPrice);
  }

  /**
   * Derive the price from the {@link selections} and {@link quantity}.
   *
   * @returns a {@link Money} object representing the total price of this bundle customizer
   */
  calculateTotal(applyGreatestHitsDiscount: boolean = false) {
    return Money.multiply(this.calculatePrice(applyGreatestHitsDiscount), this.quantity);
  }

  get canAddToCart(): boolean {
    return this.ingredientLimits.min <= this.selections.filter(isDefined).length;
  }

  /**
   * To drive displays of savings amount
   */
  get comparisonPrice() {
    const selections = this.selections.filter(isDefined);

    const listPrice = getListPrice(this.prices).value;
    let basePriceByPortion = listPrice;
    if (cents(basePriceByPortion) && selections.length < INGREDIENT_LIMITS.min) {
      basePriceByPortion = Money.multiply(
        Money.divide(listPrice, INGREDIENT_LIMITS.min),
        selections.length,
      );
    }

    const selectionsPrice = Money.sumBy(selections, (s) => getListPrice(s.prices).value);
    return Money.add(basePriceByPortion, selectionsPrice);
  }

  get currentPriceValue() {
    return getMoneyValue(this.basePrice, this.quantity);
  }

  encodeCustomizerSelections(): string {
    const skus = this.selections.filter(isDefined).map((s) => s.sku);
    return skus.join(ENCODING_DELIMITER);
  }

  prefillSelections(options: SkuIngredient[], encodedSkus?: string) {
    const skuList = (encodedSkus ?? '').split(ENCODING_DELIMITER).filter(Boolean);
    if (!skuList.length) return;

    const prefillLength = Math.max(skuList.length, this.ingredientLimits.min);
    const limit = Math.min(prefillLength, this.ingredientLimits.max);

    const parsedSelections: typeof this.selections = new Array(limit).fill(undefined);
    skuList.slice(0, limit).forEach((sku, i) => {
      const ingredient = options.find((o) => o.sku === sku);
      if (ingredient) parsedSelections[i] = SkuIngredient.toSelection(ingredient);
    });
    this.selections = parsedSelections;
  }

  setSelection(slot: number, ingredient?: SkuIngredient) {
    let selection;
    if (ingredient) {
      selection = SkuIngredient.toSelection(ingredient);
    }
    this.selections[slot] = selection;
  }
}
